import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditableProcedureData } from "../../../../types/editableTreatmentPlan";
import SurfaceSelectionSelect from "../../../../components/SurfaceSelectionSelect";
import { TreatmentStepEditChips } from "../TreatmentStepChips";
import { EditAddCodeButtonWrapper } from "./EditTreatmentAppointment.styled";
import { hasMissingSurface } from "../../../../utils/toothSurfacesUtils";
import {
  canSelectSurfaces,
  getDefaultSurfaceTab,
} from "../../../../utils/treatAreaCapabilities";

export interface EditProcedureSurfaceFieldProps {
  appointmentId: number;
  procedureIndex: number;
  procedure: EditableProcedureData;
  onChange: (
    surfaces: string[],
    meta?: { clearTeeth?: boolean },
  ) => void;
}

const EditProcedureSurfaceField: React.FC<EditProcedureSurfaceFieldProps> = ({
  appointmentId,
  procedureIndex,
  procedure,
  onChange,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasCdtCode = procedure.resolvedCdtCodes.length > 0;
  const capabilities = procedure.treatAreaCapabilities;
  const surfaceSelectionAllowed = canSelectSurfaces(capabilities);
  const isDisabled = !hasCdtCode || !surfaceSelectionAllowed;
  const disabledReason = !hasCdtCode
    ? t("treatmentPlan.selectCdtCodeFirst")
    : t("treatmentPlan.cannotSelectWithTheseCodes");
  const showRequired =
    hasCdtCode &&
    surfaceSelectionAllowed &&
    hasMissingSurface(
      procedure.teeth,
      procedure.surfaces,
      capabilities,
    );

  const handleRemove = (value: string) => {
    if (isDisabled) return;
    onChange(procedure.surfaces.filter((surface) => surface !== value));
  };

  return (
    <EditAddCodeButtonWrapper>
      <TreatmentStepEditChips
        values={procedure.surfaces}
        onRemove={handleRemove}
        onAddClick={() => setOpen(true)}
        disabled={isDisabled}
        disabledReason={disabledReason}
        error={showRequired}
        errorReason={t("treatmentPlan.valueRequired")}
      />
      <SurfaceSelectionSelect
        hideTrigger
        open={open && !isDisabled}
        onOpenChange={(nextOpen) => {
          if (isDisabled) {
            setOpen(false);
            return;
          }
          setOpen(nextOpen);
        }}
        name={`surface-${appointmentId}-${procedureIndex}`}
        value={procedure.surfaces}
        onChange={onChange}
        allowedTabs={capabilities.allowedSurfaceTabs}
        initialTab={getDefaultSurfaceTab(capabilities)}
      />
    </EditAddCodeButtonWrapper>
  );
};

export default EditProcedureSurfaceField;
